<template>
    <CompassTableRow
        class="activities-listing-table__row"
        :isSpotlight="isActivitySelected"
        :checkbox="{
            value: `${activity.type}_${activity.activityId}`,
            dataBot: `activities-listing-checkbox__${activity.activityId}`,
        }"
    >
        <template v-for="column in columns">
            <!-- DATE -->
            <CompassTableBodyCell
                v-if="column.code === 'due_date'"
                :key="column.code"
                :class="dateContainerClasses"
            >
                <BadgeActivityStatus
                    v-if="activityIsLate"
                    :tag="tag(activity.type, activity.isLate)"
                />
                <span :class="isBoldDateLabel(activity.type, activity.isLate)">
                    {{ activity.date | dateFormatter('SLASHED_DATE_TIME') }}
                </span>
            </CompassTableBodyCell>
            <!-- TYPE -->
            <CompassTableBodyCell
                v-if="column.code === 'type'"
                :key="column.code"
                minWidth="92px"
                maxWidth="92px"
            >
                <CompassObject :size="sizeObjectType" :object="objectType(activity.type)" />
            </CompassTableBodyCell>

            <!-- PRIORITY -->
            <CompassTableBodyCell
                v-if="column.code === 'priority'"
                :key="column.code"
                minWidth="108px"
                maxWidth="108px"
            >
                <ActivitiesPriorityIconButton
                    :isPending="activity.prioritisation.pending"
                    :isActive="activity.prioritisation.priority > 0"
                    :size="sizePriorityButton"
                    :enabled="activity.acl.can_be_updated"
                    dataBot="activities-listing__priority-icon-button"
                    @clicked="handlePriority"
                />
            </CompassTableBodyCell>

            <!-- LABEL -->
            <CompassTableBodyCell
                v-if="column.code === 'label'"
                :key="column.code"
                minWidth="160px"
                maxWidth="160px"
                class="label__container"
            >
                <CompassTag
                    v-if="activity.label && activity.label.label"
                    type="dot"
                    color="custom"
                    :size="sizeLabelTag"
                    :customColor="getLabelColor(activity.label)"
                >
                    {{ activity.label.label }}
                </CompassTag>
            </CompassTableBodyCell>

            <!-- RELATED -->
            <CompassTableBodyCell
                v-if="column.code === 'related'"
                :key="column.code"
                minWidth="320px"
                maxWidth="580px"
                class="related__container"
                :class="classesRelatedContainer"
            >
                <ActivitiesListingRelatedItem
                    v-for="related in activity.related"
                    :key="`${related.type}_${related.id}`"
                    :data="related"
                    :isActivityPage="isActivityPage"
                />
            </CompassTableBodyCell>
            <!-- DESCRIPTION -->
            <CompassTableBodyCell
                v-if="column.code === 'description'"
                :key="column.code"
                minWidth="480px"
                maxWidth="720px"
                class="description__container"
            >
                <div
                    v-if="activity.description"
                    class="typographies-paragraph-medium-regular"
                    v-html="activity.description"
                />
            </CompassTableBodyCell>
            <!-- TITLE -->
            <CompassTableBodyCell
                v-if="column.code === 'title'"
                :key="column.code"
                minWidth="220px"
                maxWidth="220px"
                class="title__container"
            >
                <span v-if="activity.title" class="typographies-paragraph-medium-regular">
                    {{ activity.title }}
                </span>
            </CompassTableBodyCell>

            <!-- STAFF -->
            <CompassTableBodyCell
                v-if="column.code === 'staff'"
                :key="column.code"
                minWidth="140px"
                maxWidth="168px"
            >
                <CompassMultiAvatar :avatars="formattedStaff" />
            </CompassTableBodyCell>
        </template>
        <CompassTableBodyCell
            ref="cell-actions"
            class="activities-listing__cell-actions"
            :maxWidth="`${widthActionsCol}px`"
            :minWidth="`${minWidthActionsCol}px`"
            fixed="right"
            @click.stop
            @mouseenter="isActivityPage ? onEnterActionsCell() : null"
            @mouseleave="isActivityPage ? onLeaveActionsCell() : null"
        >
            <ActivitiesListingActions
                ref="wrapper-icon-buttons"
                class="actions__container"
                :activity="activity"
                :isActivityPage="isActivityPage"
                @updateStatus="$emit('updateStatus', { activity })"
                @close="
                    (data) => {
                        $emit('onPanelClosed', data);
                    }
                "
                @updateDueDate="
                    (data) => {
                        $emit('updateDueDate', data);
                    }
                "
            />
        </CompassTableBodyCell>
    </CompassTableRow>
</template>

<script>
const MIN_WIDTH_ACTIONS_ACTIVITY_PAGE = '104';
const MIN_WIDTH_ACTIONS_DASHBOARD = '88';

import BadgeActivityStatus from 'vcomponents/utilities/badge/BadgeActivityStatus.vue';
import ActivitiesListingRelatedItem from 'vcomponents/activities/listing/ActivitiesListingRelatedItem.vue';
import ActivitiesListingActions from 'vcomponents/activities/listing/ActivitiesListingActions.vue';
import ActivitiesPriorityIconButton from 'vcomponents/activities/priorityIconButton/ActivitiesPriorityIconButton.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapGetters } = createNamespacedHelpers('activitiesStores');
export default {
    components: {
        ActivitiesListingRelatedItem,
        ActivitiesListingActions,
        ActivitiesPriorityIconButton,
        BadgeActivityStatus,
    },
    props: {
        columns: {
            type: Array,
            required: true,
        },
        activity: {
            type: Object,
            required: true,
        },
        isActivityPage: {
            type: Boolean,
            default: false,
        },
        sizeTable: {
            type: String,
            default: 'large',
        },
        widthActionsCol: {
            type: String,
            default: '0',
        },
    },
    data() {
        return {
            minWidthActionsCol: '0',
            maxWidthActionsCol: '0',
            labels: {
                owner: this.$t.__('common.labels.owner'),
            },
        };
    },
    computed: {
        ...mapGetters({ metas: 'getMetas', activitySelected: 'getActivitySelected' }),
        isActivitySelected() {
            return (
                this.activitySelected &&
                this.activitySelected.id === this.activity.activityId &&
                this.activitySelected.type === this.activity.type
            );
        },
        sizeObjectType() {
            return this.sizeTable === 'large' ? 'small' : 'extra-small';
        },
        sizeLabelTag() {
            return this.sizeTable === 'large' ? 'medium' : 'small';
        },
        sizePriorityButton() {
            return this.sizeTable === 'large' ? 'medium' : 'small';
        },
        formattedStaff() {
            return this.activity.staff.map((staff) => {
                return {
                    ...staff,
                    fullname:
                        staff.stateBadge === 'owner'
                            ? `${staff.fullname} (${this.$t.__('common.labels.assignee')})`
                            : staff.fullname,
                };
            });
        },
        classesRelatedContainer() {
            return [this.isActivityPage ? '' : 'related__container--dashboard'];
        },
        dateContainerClasses() {
            return ['date__container', this.activityIsLate ? 'date__container--late' : ''];
        },
        activityIsLate() {
            return this.isLate(this.activity.type, this.activity.isLate);
        },
    },
    created() {
        this.minWidthActionsCol = this.isActivityPage
            ? MIN_WIDTH_ACTIONS_ACTIVITY_PAGE
            : MIN_WIDTH_ACTIONS_DASHBOARD;
        this.$emit('update:widthActionsCol', this.minWidthActionsCol);
    },
    mounted() {
        this.calculateMaxWidthActionsCol();
    },
    methods: {
        calculateMaxWidthActionsCol() {
            const refWrapperIconButtons = this.$refs['wrapper-icon-buttons'];
            const actionsContainer = this.$refs['cell-actions'].$el.childNodes[0];

            let width = 0;
            for (const { $el: childNode } of refWrapperIconButtons.$children) {
                width += childNode?.clientWidth ?? 0;
            }

            const gapBetweenIconButtons =
                refWrapperIconButtons.$el.style.gap.replace('px', '') *
                (refWrapperIconButtons.$children.length - 1);

            const paddingParent =
                window
                    .getComputedStyle(actionsContainer)
                    .getPropertyValue('padding-left')
                    .replace('px', '') * 2;

            this.maxWidthActionsCol = (width + gapBetweenIconButtons + paddingParent).toString();
        },
        onEnterActionsCell() {
            if (this.maxWidthActionsCol !== '0') {
                this.$emit('update:widthActionsCol', this.maxWidthActionsCol);
                return;
            }
            this.$emit('update:widthActionsCol', this.minWidthActionsCol);
        },
        onLeaveActionsCell() {
            const actionsCellHovered = document.querySelector(
                'td.activities-listing__cell-actions:hover',
            );
            if (!!actionsCellHovered) {
                return;
            }
            this.$emit('update:widthActionsCol', this.minWidthActionsCol);
        },
        isEmailOrEvent(type) {
            return ['email', 'event'].includes(type);
        },
        isLate(type, lateness) {
            return !this.isEmailOrEvent(type) && lateness;
        },
        tag(type, lateness) {
            //TODO : email case
            return this.isLate(type, lateness)
                ? { type: 'error', label: this.$l10n.__('En retard') }
                : {};
        },
        isBoldDateLabel(type, lateness) {
            return this.isLate(type, lateness)
                ? 'typographies-paragraph-medium-bold'
                : 'typographies-paragraph-medium-regular';
        },
        objectType(type) {
            switch (type) {
                case 'event':
                    return 'calendar';
                case 'phonecall':
                    return 'phone';
                case 'email':
                    return 'sent-email';
                default:
                    return type;
            }
        },
        getLabelColor(label) {
            if (label.color) {
                return label.color;
            }
            try {
                const {
                    task: { labels },
                } = this.metas;
                const labelFound = labels.find((item) => item.label === label.label);
                return labelFound.color;
            } catch {
                return '#000000';
            }
        },
        handlePriority() {
            this.$emit('updatePriority', {
                priority: this.activity.prioritisation.priority,
                id: this.activity.activityId,
                type: this.activity.type,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.activities-listing-table__row {
    cursor: pointer;
    .date {
        &__container::v-deep {
            .compass-table__cell {
                &-container {
                    min-width: 160px;
                    display: flex;
                    gap: var(--spacing-0);
                    justify-content: center;
                }
            }
        }
        &__container--late::v-deep {
            .compass-table__cell-container {
                align-items: flex-start;
                flex-direction: column;
            }
        }
        &__label {
            display: flex;
        }
    }

    .description__container::v-deep {
        p {
            margin: 0;
        }
    }
    .label__container::v-deep .compass-tag__dot {
        flex-shrink: 0;
    }

    .related__container {
        &::v-deep > .compass-table__cell-container {
            flex-wrap: wrap;
            gap: var(--spacing-1);
        }
        &--dashboard::v-deep > .compass-table__cell-container {
            align-items: flex-start;
            flex-direction: column;
            justify-content: center;
            &:has(.activity-listing-related-item-align:only-child) {
                align-items: center;
                flex-direction: row;
            }
        }
    }

    .activities-listing__cell-actions {
        transition: max-width 200ms ease-in-out, min-width 200ms ease-in-out;
        &::v-deep > .compass-table__cell-container {
            overflow: hidden;
            flex-direction: row-reverse;
        }
    }
}
</style>
